import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Plus, Camera, Upload, X, Trash2, Pencil, Check, AlertCircle, Loader2 } from 'lucide-react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { productsApi } from '../services/api';
import {
  loadCLIPModel,
  isModelLoaded,
  generateEmbedding,
  averageEmbeddings,
  embeddingToBase64,
  resizeAndCompressImage,
  cropImage,
} from '../services/clipEmbedding';
import type { Product } from '../types';

type PhotoAngle = 'front' | 'left' | 'right';

interface PhotoCapture {
  original: string | null;
  cropped: string | null;
  embedding: Float32Array | null;
}

export default function ProductSetup() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [filterUnknown, setFilterUnknown] = useState(false);
  const queryClient = useQueryClient();

  // Add product form state
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productSku, setProductSku] = useState('');
  const [currentAngle, setCurrentAngle] = useState<PhotoAngle>('front');
  const [photos, setPhotos] = useState<Record<PhotoAngle, PhotoCapture>>({
    front: { original: null, cropped: null, embedding: null },
    left: { original: null, cropped: null, embedding: null },
    right: { original: null, cropped: null, embedding: null },
  });
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [modelStatus, setModelStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Load products
  const { data: products, isLoading } = useQuery({
    queryKey: ['products', filterUnknown],
    queryFn: () => productsApi.list({ unknown_only: filterUnknown }),
  });

  // Load CLIP model on mount
  const loadModel = useCallback(async () => {
    try {
      setModelStatus('loading');
      await loadCLIPModel();
      setModelStatus('ready');
    } catch (error) {
      console.error('Failed to load CLIP model:', error);
      setModelStatus('error');
    }
  }, []);

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
      resetAddForm();
      setShowAddModal(false);
    },
    onError: () => {
      toast.error('Failed to create product');
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; category?: string | null; sku?: string | null } }) =>
      productsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated');
      setEditingName(false);
      setSelectedProduct(null);
    },
    onError: () => {
      toast.error('Failed to update product');
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: productsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted');
      setShowDeleteConfirm(false);
      setSelectedProduct(null);
    },
    onError: () => {
      toast.error('Failed to delete product');
    },
  });

  const resetAddForm = () => {
    setProductName('');
    setProductCategory('');
    setProductSku('');
    setCurrentAngle('front');
    setPhotos({
      front: { original: null, cropped: null, embedding: null },
      left: { original: null, cropped: null, embedding: null },
      right: { original: null, cropped: null, embedding: null },
    });
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      // Resize before storing
      const resized = await resizeAndCompressImage(dataUrl, 1200, 0.9);
      setPhotos((prev) => ({
        ...prev,
        [currentAngle]: { ...prev[currentAngle], original: resized, cropped: null, embedding: null },
      }));
      setCrop(undefined);
      setCompletedCrop(undefined);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imageRef.current || !photos[currentAngle].original) return;
    await processImage(photos[currentAngle].original!, completedCrop);
  };

  const handleUseFullImage = async () => {
    if (!photos[currentAngle].original) return;
    // Pass null to use full image
    await processImage(photos[currentAngle].original!, null);
  };

  const processImage = async (originalImage: string, cropArea: PixelCrop | null) => {
    setIsProcessing(true);
    try {
      // Make sure model is loaded
      if (!isModelLoaded()) {
        setModelStatus('loading');
        await loadCLIPModel();
        setModelStatus('ready');
      }

      let imageToProcess: string;

      if (cropArea) {
        // Crop the image
        const cropped = await cropImage(originalImage, {
          x: cropArea.x,
          y: cropArea.y,
          width: cropArea.width,
          height: cropArea.height,
        });
        imageToProcess = cropped;
      } else {
        // Use full image
        imageToProcess = originalImage;
      }

      // Resize for storage
      const resizedImage = await resizeAndCompressImage(imageToProcess, 400, 0.8);

      // Generate embedding
      const embedding = await generateEmbedding(resizedImage);

      if (!embedding || embedding.length === 0) {
        throw new Error('Failed to generate embedding - empty result');
      }

      setPhotos((prev) => ({
        ...prev,
        [currentAngle]: { ...prev[currentAngle], cropped: resizedImage, embedding },
      }));

      toast.success(`${currentAngle.charAt(0).toUpperCase() + currentAngle.slice(1)} photo processed`);

      // Move to next angle if not all done
      const angles: PhotoAngle[] = ['front', 'left', 'right'];
      const currentIndex = angles.indexOf(currentAngle);
      if (currentIndex < angles.length - 1) {
        setCurrentAngle(angles[currentIndex + 1]);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to process image: ${errorMessage}`);
      setModelStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!productName.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    // Check all photos are captured
    const allPhotosReady = photos.front.embedding && photos.left.embedding && photos.right.embedding;
    if (!allPhotosReady) {
      toast.error('Please capture all 3 photos (front, left, right)');
      return;
    }

    setIsProcessing(true);
    try {
      // Average the embeddings
      const avgEmbedding = averageEmbeddings([
        photos.front.embedding!,
        photos.left.embedding!,
        photos.right.embedding!,
      ]);

      // Create the product
      await createMutation.mutateAsync({
        name: productName.trim(),
        category: productCategory.trim() || null,
        sku: productSku.trim() || null,
        descriptor: embeddingToBase64(avgEmbedding),
        thumbnails: [photos.front.cropped!, photos.left.cropped!, photos.right.cropped!],
      });
    } catch (error) {
      console.error('Error creating product:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getCompletedCount = () => {
    let count = 0;
    if (photos.front.cropped) count++;
    if (photos.left.cropped) count++;
    if (photos.right.cropped) count++;
    return count;
  };

  const angleLabels: Record<PhotoAngle, string> = {
    front: 'Front View',
    left: 'Left Angle',
    right: 'Right Angle',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Product Setup</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">Teach the system to recognize your products</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant={filterUnknown ? 'primary' : 'secondary'}
            onClick={() => setFilterUnknown(!filterUnknown)}
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            {filterUnknown ? 'Showing Unknown' : 'Show Unknown Only'}
          </Button>
          <Button onClick={() => { resetAddForm(); loadModel(); setShowAddModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : products?.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No products yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">
            Add products by taking photos from different angles. The system will learn to recognize them.
          </p>
          <Button onClick={() => { resetAddForm(); loadModel(); setShowAddModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Product
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {products?.map((product) => (
            <Card
              key={product.id}
              className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
              onClick={() => setSelectedProduct(product)}
            >
              {/* Thumbnail */}
              <div className="aspect-square bg-gray-100 dark:bg-slate-700 relative">
                {product.thumbnails?.[0] ? (
                  <img
                    src={product.thumbnails[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="h-12 w-12 text-gray-400 dark:text-slate-500" />
                  </div>
                )}
                {product.is_unknown && (
                  <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                    Needs Review
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="p-3">
                <h3 className="font-medium text-gray-900 dark:text-white truncate">{product.name}</h3>
                {product.category && (
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{product.category}</p>
                )}
                {product.sku && (
                  <p className="text-xs text-gray-400 dark:text-slate-500 truncate">SKU: {product.sku}</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold dark:text-white">Add New Product</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Info */}
              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Product Name *"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g., Chanel No. 5"
                />
                <Input
                  label="Category"
                  value={productCategory}
                  onChange={(e) => setProductCategory(e.target.value)}
                  placeholder="e.g., Perfume"
                />
                <Input
                  label="SKU"
                  value={productSku}
                  onChange={(e) => setProductSku(e.target.value)}
                  placeholder="e.g., CHN5-100ML"
                />
              </div>

              {/* Model Status */}
              {modelStatus === 'loading' && (
                <div className="bg-blue-50 text-blue-700 p-3 rounded-lg flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading AI model for product recognition...
                </div>
              )}
              {modelStatus === 'error' && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Failed to load AI model. Please refresh and try again.
                </div>
              )}

              {/* Photo Progress */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-slate-300">Photos: {getCompletedCount()}/3</span>
                <div className="flex gap-2">
                  {(['front', 'left', 'right'] as PhotoAngle[]).map((angle) => (
                    <button
                      key={angle}
                      onClick={() => setCurrentAngle(angle)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        currentAngle === angle
                          ? 'bg-blue-600 text-white'
                          : photos[angle].cropped
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                      }`}
                    >
                      {photos[angle].cropped && <Check className="h-3 w-3 inline mr-1" />}
                      {angleLabels[angle]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo Capture */}
              <div className="border dark:border-slate-700 rounded-lg p-4">
                <h3 className="font-medium dark:text-white mb-3">{angleLabels[currentAngle]}</h3>

                {!photos[currentAngle].original ? (
                  <div className="flex gap-4 justify-center py-8">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button variant="secondary" onClick={() => cameraInputRef.current?.click()}>
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                    <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                ) : photos[currentAngle].cropped ? (
                  <div className="text-center">
                    <img
                      src={photos[currentAngle].cropped!}
                      alt={`${currentAngle} cropped`}
                      className="max-h-64 mx-auto rounded-lg"
                    />
                    <Button
                      variant="secondary"
                      className="mt-4"
                      onClick={() => {
                        setPhotos((prev) => ({
                          ...prev,
                          [currentAngle]: { original: null, cropped: null, embedding: null },
                        }));
                      }}
                    >
                      Retake Photo
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-slate-300 mb-2">
                      Draw a box around the product to crop it, or use the full image:
                    </p>
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      className="max-h-[400px] mx-auto"
                    >
                      <img
                        ref={imageRef}
                        src={photos[currentAngle].original!}
                        alt="Product"
                        className="max-h-[400px]"
                      />
                    </ReactCrop>
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPhotos((prev) => ({
                            ...prev,
                            [currentAngle]: { original: null, cropped: null, embedding: null },
                          }));
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleUseFullImage}
                        disabled={isProcessing || modelStatus !== 'ready'}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Processing...
                          </>
                        ) : (
                          'Use Full Image'
                        )}
                      </Button>
                      <Button
                        onClick={handleCropComplete}
                        disabled={!completedCrop || isProcessing || modelStatus !== 'ready'}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Processing...
                          </>
                        ) : (
                          'Confirm Crop'
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t dark:border-slate-700 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateProduct}
                disabled={
                  isProcessing ||
                  !productName.trim() ||
                  !photos.front.embedding ||
                  !photos.left.embedding ||
                  !photos.right.embedding
                }
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Product'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold dark:text-white">Product Details</h2>
              <button onClick={() => setSelectedProduct(null)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Thumbnails */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {selectedProduct.thumbnails?.map((thumb, i) => (
                  <div key={i} className="aspect-square bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                    <img src={thumb} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                )) || (
                  <div className="col-span-3 aspect-video bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                    <Package className="h-16 w-16 text-gray-400 dark:text-slate-500" />
                  </div>
                )}
              </div>

              {/* Name */}
              <div className="mb-4">
                {editingName ? (
                  <div className="flex gap-2">
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Product name"
                    />
                    <Button
                      onClick={() => {
                        updateMutation.mutate({ id: selectedProduct.id, data: { name: newName } });
                      }}
                      disabled={updateMutation.isPending}
                    >
                      Save
                    </Button>
                    <Button variant="secondary" onClick={() => setEditingName(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-semibold dark:text-white">{selectedProduct.name}</h3>
                    <button
                      onClick={() => {
                        setNewName(selectedProduct.name);
                        setEditingName(true);
                      }}
                      className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {selectedProduct.is_unknown && (
                      <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded">
                        Needs Review
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm dark:text-slate-200">
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Category:</span>
                  <span className="ml-2">{selectedProduct.category || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">SKU:</span>
                  <span className="ml-2">{selectedProduct.sku || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Sample Count:</span>
                  <span className="ml-2">{selectedProduct.sample_count}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Created:</span>
                  <span className="ml-2">{new Date(selectedProduct.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t dark:border-slate-700 flex justify-between">
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Product
              </Button>
              <Button variant="secondary" onClick={() => setSelectedProduct(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-semibold dark:text-white mb-2">Delete Product?</h3>
            <p className="text-gray-600 dark:text-slate-300 mb-4">
              Are you sure you want to delete "{selectedProduct.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(selectedProduct.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
